import React, { useCallback, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import useAdminToken from "../hooks/useAdminToken";
import CheckoutV2Editor, { flagOf, pageUrl } from "../components/checkout-v2-editor";
/* The registry drives both tab rows. There is no country list and no day list
 * in this file — adding Thailand to functions/countries.js grows a tab here on
 * the next build, which is the whole promise the registry makes. */
import { COUNTRIES, DAYS } from "../utils/countries";

/* Admin for the multi-region checkout v2 pages.
 *
 * Deliberately a SECOND screen rather than a rewrite of /admin/checkout: that
 * one stays live and untouched as the safety net while v2 is proven, and both
 * are labelled loudly so nobody edits the wrong one during a webinar. Malaysia
 * is the overlap — the registry points it at the same Firestore docs as v1, so
 * editing it on either screen changes both, which is the point.
 *
 * Same passphrase gate as /admin/checkout, sharing its session token, so
 * verifying on one covers the other. Nothing new was invented here: this route
 * is exactly as protected as the one it will replace. */

export default function AdminCheckoutV2() {
  const { token, verified, busy, verify, clear } = useAdminToken();
  const [keyInput, setKeyInput] = useState("");

  /* Day and country live in the query string, not in state: a refresh mid-edit —
     or a link pasted to someone else — lands back on the same tab instead of
     dropping to Day 1 / Malaysia. Unknown values fall back rather than throw,
     so a hand-typed ?country=zz still renders something. Replace, not push, so
     switching tabs doesn't fill the back button with history. */
  const [searchParams, setSearchParams] = useSearchParams();
  const day = DAYS.some((d) => String(d) === searchParams.get("day"))
    ? Number(searchParams.get("day"))
    : DAYS[0];
  const country = COUNTRIES.some((c) => c.code === searchParams.get("country"))
    ? searchParams.get("country")
    : COUNTRIES[0].code;

  const setParam = useCallback(
    (key, value) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(key, String(value));
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // Publish lives in the header row but belongs to the editor's form state, so
  // the editor hands its action up here and reports what it is busy doing.
  const publishRef = useRef(null);
  const [editorStatus, setEditorStatus] = useState({
    publishing: false,
    loading: true,
  });
  const activeCountry = COUNTRIES.find((c) => c.code === country) || COUNTRIES[0];

  const handleVerify = async (e) => {
    e.preventDefault();
    try {
      await verify(keyInput);
      setKeyInput("");
      toast.success("Verified");
    } catch (err) {
      toast.error(String((err && err.message) || err));
    }
  };

  if (!verified) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="card bg-base-100 shadow w-full max-w-md">
          <div className="card-body">
            <h3 className="font-bold text-lg">Admin Verification</h3>
            <p className="py-2 text-sm">
              Enter the admin key to edit the checkout v2 pages.
            </p>
            <form onSubmit={handleVerify}>
              <input
                type="password"
                className="input input-bordered w-full"
                value={keyInput}
                onChange={(e) => setKeyInput(e.target.value)}
                placeholder="Admin key"
                autoFocus
              />
              <div className="modal-action">
                <button className="btn btn-primary" disabled={busy}>
                  {busy && <span className="loading loading-spinner loading-xs" />}
                  Verify
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-3 sm:p-4">
      <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold">
            Checkout v2 <span className="opacity-40">·</span> Multi-region
          </h1>
          {/* Two live admin screens is a foot-gun until /checkout/ is switched
              off, so each one says which pages it edits and links to the other
              rather than leaving that to memory. */}
          <p className="text-sm opacity-70">
            Edits <code>/checkout-v2-day1/</code> and{" "}
            <code>/checkout-v2-day2/</code> for all {COUNTRIES.length} countries.
            Sales and fees are on{" "}
            <a className="link" href="/admin/checkout-v2-orders">
              /admin/checkout-v2-orders
            </a>
            ; the current single-country pages are edited on{" "}
            <a className="link" href="/admin/checkout?tab=offer">
              /admin/checkout
            </a>
            .
          </p>
        </div>
        <div className="flex gap-2 flex-wrap w-full sm:w-auto">
          <button className="btn btn-outline btn-error btn-sm" onClick={clear}>
            Re-verify
          </button>
        </div>
      </div>

      {/* Day first, then country: the day is the bigger switch — it reloads the
          form and drops unpublished edits — while country only changes which
          price block is on screen. They are separate rows so a hurried click
          can't land on the wrong one. */}
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <div role="tablist" className="tabs tabs-boxed w-full sm:w-fit">
          {DAYS.map((d) => (
            <button
              key={d}
              role="tab"
              type="button"
              className={`tab flex-1 sm:flex-none ${d === day ? "tab-active" : ""}`}
              aria-selected={d === day}
              onClick={() => setParam("day", d)}
              // Locked mid-publish so a stray click cannot swap the form out
              // from under the request.
              disabled={editorStatus.publishing}
            >
              Day {d}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto sm:ml-auto">
          <a
            href={pageUrl(day, activeCountry.code)}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-ghost btn-square"
            title={`View ${pageUrl(day, activeCountry.code)}`}
            aria-label={`View day ${day} ${activeCountry.label}`}
          >
            {/* Eye — follows the selected day and country, so there is one
                button here instead of one per variant. */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-5 h-5"
              aria-hidden="true"
            >
              <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          </a>
          {/* Says "Day N", not "Day N · MY": one publish writes every country
              whose price or shared content changed, and a button naming only
              the visible tab would badly understate that. */}
          <button
            className="btn btn-primary flex-1 sm:flex-none"
            onClick={() => publishRef.current && publishRef.current()}
            disabled={editorStatus.publishing || editorStatus.loading}
          >
            {editorStatus.publishing && (
              <span className="loading loading-spinner loading-xs" />
            )}
            Publish Day {day}
          </button>
        </div>
      </div>

      {/* Country tabs, straight off the registry. */}
      <div role="tablist" className="tabs tabs-boxed mb-4 w-full sm:w-fit">
        {COUNTRIES.map((c) => (
          <button
            key={c.code}
            role="tab"
            type="button"
            className={`tab flex-1 sm:flex-none gap-1 ${
              c.code === country ? "tab-active" : ""
            }`}
            aria-selected={c.code === country}
            onClick={() => setParam("country", c.code)}
            title={`${c.label} — ${c.currency}, ${c.gateway}`}
          >
            <span aria-hidden="true">{flagOf(c.code)}</span>
            {c.code.toUpperCase()}
          </button>
        ))}
      </div>

      <CheckoutV2Editor
        token={token}
        day={day}
        country={country}
        onStatusChange={setEditorStatus}
        publishRef={publishRef}
      />
    </div>
  );
}
